import type { MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { css, cx } from "styled-system/css";

/**
 * Logo de marca SFTURNOS (ícono + wordmark). Único punto de verdad reutilizado
 * en navbar de landing, sidebar/header del dashboard y login — evita duplicar
 * el `<img>` + texto en cada lugar.
 */
export function Logo({
  to,
  size = 28,
  textSize = "15px",
  onDark = false,
  gap = "10px",
  className,
  onClick,
}: {
  /** Si se pasa, el logo navega a esta ruta (misma pestaña). Si se omite, es sólo visual. */
  to?: string;
  /** Tamaño del ícono en px. */
  size?: number;
  /** fontSize del wordmark (string CSS, ej. "15px"). */
  textSize?: string;
  /** true = wordmark blanco (fondos oscuros: sidebar, header, login). false = wordmark oscuro (fondos claros: navbar). */
  onDark?: boolean;
  /** Espacio entre ícono y wordmark (string CSS con unidad, ej. "10px"). */
  gap?: string;
  className?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const content: ReactNode = (
    <>
      <img src="/logo-mark.png" alt="" width={size} height={size} style={{ display: "block", flexShrink: 0 }} />
      <span
        style={{
          fontWeight: 700,
          letterSpacing: "0.12em",
          fontSize: textSize,
          lineHeight: 1,
          color: onDark ? "rgba(255,255,255,0.96)" : "#0f172a",
          whiteSpace: "nowrap",
        }}
      >
        SFTURNOS
      </span>
    </>
  );

  // `gap` es un prop dinámico (no un literal que Panda pueda extraer en build-time),
  // así que va en `style` — con unidad CSS válida (ej. "10px"), a diferencia del bug
  // anterior que pasaba un token de Panda ("2.5") sin unidad, que el navegador ignora.
  const rowClass = cx(css({ display: "inline-flex", alignItems: "center", minW: 0 }), className);

  if (to) {
    return (
      <Link
        to={to}
        aria-label="SFTURNOS — Ir al inicio"
        className={cx(rowClass, css({ textDecoration: "none" }))}
        style={{ gap }}
        onClick={onClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <div className={rowClass} style={{ gap }} aria-label="SFTURNOS">
      {content}
    </div>
  );
}
